import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { navigate } from 'wouter-preact/use-browser-location'
import { useRoute } from 'wouter-preact'

import { InboxIcon, Typography } from 'components/UI/atoms'
import {
	clearAllNotifications,
	notificationsState,
	removeNotification
} from 'state'

const Notifications = () => {
	const [isOnWorkspacePage] = useRoute('/smart-contract/:dAppName/workspace')

	// We can close the popover panel by calling `close()` from the render props of <PopoverPanel>.
	const handleClickNotification = (
		notificationId: string,
		workspaceURL: string,
		close: () => void
	) => {
		if (isOnWorkspacePage) {
			window.open(workspaceURL, '_blank', 'noopener noreferrer')
		} else {
			navigate(workspaceURL)
		}

		removeNotification(notificationId)
		close()
	}

	return (
		<Popover class='relative flex'>
			{/* The trigger button for the popover */}
			<PopoverButton
				aria-label='Toggle notifications'
				class='relative focus-visible:outline-none'
			>
				<InboxIcon class='h-4 w-4' />
				{notificationsState.value.length > 0 && (
					<div class='absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-product text-[10px] text-black-900'>
						{notificationsState.value.length}
					</div>
				)}
			</PopoverButton>

			{/* The notifications panel */}
			<PopoverPanel
				class='
          absolute right-1 z-10 mt-6 w-96 overflow-hidden rounded-lg
          border border-black-600 bg-black-900 shadow-lg
          transition duration-200
          ease-in-out data-[closed]:-translate-y-1 data-[closed]:opacity-0
        '
				transition
			>
				{({ close }) => (
					<>
						{/* If there are notifications, show 'Mark all as read' */}
						{notificationsState.value.length !== 0 && (
							<div class='flex justify-end border-b border-gray-700 p-4'>
								<button
									aria-label='Read all notifications'
									class='text-xs font-semibold leading-[14.4px] text-product hover:underline'
									onClick={clearAllNotifications}
									type='button'
								>
									<Typography
										color='text-product'
										text='Mark all as read'
										variant='span2'
									/>
								</button>
							</div>
						)}

						{/* If there are no notifications, show an empty state */}
						{notificationsState.value.length === 0 && (
							<div class='p-4'>
								<Typography
									color='text-black-200'
									text='You do not have any notifications yet'
									variant='text2'
								/>
							</div>
						)}

						{/* Notifications list */}
						<div class='max-h-96 overflow-y-auto'>
							{notificationsState.value.map(notification => (
								<button
									class='flex w-full items-center border-b border-black-800 p-4 focus:outline-transparent'
									key={notification.id}
									onClick={() =>
										handleClickNotification(
											notification.id,
											notification.workspaceURL,
											close
										)
									}
									type='button'
								>
									<div class='mr-6 flex flex-1 flex-col'>
										<div class='flex items-center justify-between'>
											<Typography
												class='mt-3 cursor-pointer self-start'
												color='text-product'
												text={notification.title}
												variant='label2'
											/>
											<Typography
												class='mt-3 cursor-pointer self-start'
												color='text-black-100'
												text={notification.time}
												variant='label4'
											/>
										</div>
										<Typography
											class='mt-3 cursor-pointer self-start'
											color='text-black-200'
											text={notification.description}
											variant='label4'
										/>
									</div>
									<div class='flex items-center justify-center'>
										<div class='h-2 w-2 rounded-full bg-product' />
									</div>
								</button>
							))}
						</div>
					</>
				)}
			</PopoverPanel>
		</Popover>
	)
}

export { Notifications }
